import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, Banner, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";

interface WorkSecurityArchiveConfirmModalProps {
  roleName: string;
  onConfirm: () => Promise<void>;
}

export default function WorkSecurityArchiveConfirmModal({
  roleName,
  onConfirm,
}: WorkSecurityArchiveConfirmModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const close = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setErrMsg(null);
      await onConfirm();
      close();
    } catch (e: any) {
      console.error(e);
      setErrMsg(
        e?.response?.data?.message ||
          t("failed_archive_role", { defaultValue: "Failed to archive role." })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {errMsg && <Banner tone="critical">{errMsg}</Banner>}

      <div className="py-2">
        <Text as="p" variant="bodyMd">
          {t("confirm_archive_role_desc", {
            defaultValue:
              `Archive "${roleName}"? It will be hidden from the roles list, the assignments dashboard and workers' documents, and can no longer receive new assignments. Existing signatures are kept and you can restore it anytime.`,
            roleName,
          })}
        </Text>
      </div>

      <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
        <Button onClick={close} disabled={isLoading}>
          {t("cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button onClick={handleConfirm} loading={isLoading} variant="primary" tone="critical">
          {t("archive", { defaultValue: "Archive" })}
        </Button>
      </div>
    </div>
  );
}
